import { useState } from 'react';
import { Plus, Users, Mail, DollarSign } from 'lucide-react';
import { useProjectTeamMembers } from '@/hooks/useProjects';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface ProjectTeamMembersProps {
  projectId: string;
  limit?: number;
}

export default function ProjectTeamMembers({ projectId, limit }: ProjectTeamMembersProps) {
  const { data: teamMembers, isLoading } = useProjectTeamMembers(projectId);
  
  const displayedMembers = limit ? teamMembers?.slice(0, limit) : teamMembers;

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'internal':
        return 'bg-blue-100 text-blue-800';
      case 'freelancer':
        return 'bg-green-100 text-green-800';
      case 'contractor':
        return 'bg-orange-100 text-orange-800';
      case 'vendor':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">
          {limit ? 'Team Members' : 'Project Team'}
        </h3>
        {!limit && (
          <Button size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Add Team Member
          </Button>
        )}
      </div>

      {displayedMembers && displayedMembers.length > 0 ? (
        <div className="space-y-3">
          {displayedMembers.map((member) => (
            <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <Users className="h-4 w-4 text-gray-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{member.member_name}</h4>
                    <Badge className={getTypeColor(member.member_type)}>
                      {member.member_type}
                    </Badge>
                    <Badge className={getStatusColor(member.status)}>
                      {member.status}
                    </Badge>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">{member.role}</p>
                  
                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                    {member.member_email && (
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {member.member_email}
                      </div>
                    )}
                    {member.hourly_rate && (
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        {formatCurrency(member.hourly_rate)}/hr
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                {member.total_budget && (
                  <div className="font-medium">{formatCurrency(member.total_budget)}</div>
                )}
                <div className="text-xs text-muted-foreground">Budget</div>
              </div>
            </div>
          ))}
          
          {limit && teamMembers && teamMembers.length > limit && (
            <div className="text-center">
              <Button variant="link" className="text-sm">
                View all {teamMembers.length} team members →
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Team Members</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Add team members to track project resources and costs.
          </p>
          {!limit && (
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add First Team Member
            </Button>
          )}
        </div>
      )}
    </div>
  );
}